import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components/ui/Header';
import { ErrorBoundary } from '../components/ui/ErrorBoundary';
import { StockHeroSkeleton, ChartSkeleton } from '../components/ui/Skeleton';
import ApexStockChart from '../components/charts/ApexStockChart';
import { DetailedFinancialStatements } from '../components/financial/DetailedFinancialStatements';
import { AWSAIAnalysis } from '../components/ai/AWSAIAnalysis';
import { StockNews } from '../components/news/StockNews';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../contexts/AuthContext';
import { tradierService, financialDataService } from '../services';
import { ArrowLeft, TrendingUp, TrendingDown, Activity, BarChart3, FileText, Brain, Newspaper } from 'lucide-react';
import type { StockQuote, CompanyMetrics } from '../types/market';

export const StockDetail: React.FC = () => {
  const { symbol } = useParams<{ symbol: string }>();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { showToast } = useToast();

  const [stockData, setStockData] = useState<StockQuote | null>(null);
  const [companyMetrics, setCompanyMetrics] = useState<CompanyMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey] = useState(0);

  const fetchStockData = async (ticker: string, showLoadingState = true) => {
    try {
      if (showLoadingState) { setLoading(true); setError(null); }
      const [quotes, metrics] = await Promise.allSettled([
        tradierService.instance.getQuotes([ticker.toUpperCase()]),
        financialDataService.instance.getCompanyMetrics(ticker.toUpperCase()),
      ]);
      if (quotes.status === 'fulfilled' && quotes.value.length > 0) {
        setStockData(quotes.value[0]);
      } else {
        throw new Error(`No data found for symbol: ${ticker}`);
      }
      if (metrics.status === 'fulfilled') {
        setCompanyMetrics(metrics.value);
      } else if (!companyMetrics) {
        setCompanyMetrics({ symbol: ticker.toUpperCase(), name: `${ticker.toUpperCase()} Stock`, marketCap: 0 });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch stock data';
      if (showLoadingState) { setError(msg); showToast(msg, 'error'); }
    } finally {
      if (showLoadingState) setLoading(false);
    }
  };

  const updateStockPrice = async (ticker: string) => {
    try {
      const quotes = await tradierService.instance.getQuotes([ticker.toUpperCase()]);
      if (quotes.length > 0) setStockData(quotes[0]);
    } catch { /* silent background update */ }
  };

  useEffect(() => {
    if (symbol) fetchStockData(symbol);
    else { setError('No stock symbol provided'); setLoading(false); }
  }, [symbol, refreshKey]);

  useEffect(() => {
    if (!symbol || !stockData) return;
    const iv = setInterval(() => updateStockPrice(symbol), 30000);
    return () => clearInterval(iv);
  }, [symbol, stockData]);

  const handleRefresh = async () => {
    if (symbol) {
      await updateStockPrice(symbol);
      showToast('Stock data refreshed', 'success');
    }
  };

  const fmt$  = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
  const fmtΔ  = (n: number) => `${n >= 0 ? '+' : ''}${fmt$(n)}`;
  const fmtPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;

  /* ── Loading ── */
  if (loading) {
    return (
      <ErrorBoundary>
        <div className="page-container">
          <Header user={user} onLogout={signOut} title={symbol ? `${symbol.toUpperCase()} · Nitrextrader` : 'Nitrextrader'} />
          <div style={{ maxWidth: 1440, margin: '0 auto', padding: '1.5rem 1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ height: 16, width: 120, borderRadius: 6 }} className="skeleton" />
            <div style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 14, overflow: 'hidden' }}>
              <StockHeroSkeleton />
            </div>
            <div style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 14, padding: '1.5rem' }}>
              <ChartSkeleton />
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  /* ── Error ── */
  if (error || !stockData) {
    return (
      <ErrorBoundary>
        <div className="page-container">
          <Header user={user} onLogout={signOut} title={symbol ? `${symbol.toUpperCase()} · Nitrextrader` : 'Nitrextrader'} />
          <div style={{ maxWidth: 1440, margin: '0 auto', padding: '1.5rem 1rem' }}>
            <button
              onClick={() => navigate('/dashboard')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, padding: '0.25rem 0', marginBottom: '1.5rem' }}
            >
              <ArrowLeft size={15} strokeWidth={2.5} /> Back to Dashboard
            </button>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 360 }}>
              <div style={{ textAlign: 'center', maxWidth: 360 }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
                  <Activity size={24} color="#f87171" strokeWidth={2} />
                </div>
                <h2 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '0.5rem', letterSpacing: '-0.02em' }}>Unable to Load Stock Data</h2>
                <p style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '1.25rem', lineHeight: 1.6 }}>{error}</p>
                <button
                  onClick={handleRefresh}
                  style={{ background: '#3b82f6', color: '#fff', padding: '0.625rem 1.25rem', borderRadius: 9, fontWeight: 600, fontSize: '0.875rem', border: 'none', cursor: 'pointer' }}
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  const isPos = stockData.change >= 0;
  const gainColor = '#10b981';
  const lossColor = '#ef4444';
  const changeColor = isPos ? gainColor : lossColor;
  const changeBg = isPos ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)';
  const changeBorder = isPos ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)';

  const metrics = [
    { label: 'Volume',       value: stockData.volume.toLocaleString(),              mono: true },
    { label: 'Change',       value: fmtΔ(stockData.change),     color: changeColor, mono: true },
    { label: 'Change %',     value: fmtPct(stockData.change_percent), color: changeColor, mono: true },
    { label: 'Last Updated', value: new Date(stockData.last_updated).toLocaleTimeString(), mono: false },
  ];

  return (
    <ErrorBoundary>
      <div className="page-container">
        <Header
          user={user}
          onLogout={signOut}
          onRefresh={handleRefresh}
          title={`${stockData.symbol} · Nitrextrader`}
        />

        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '1.25rem 1rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          {/* Back */}
          <button
            onClick={() => navigate('/dashboard')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, padding: '0.125rem 0', width: 'fit-content', transition: 'color 0.18s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#60a5fa'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#3b82f6'; }}
          >
            <ArrowLeft size={15} strokeWidth={2.5} /> Back to Dashboard
          </button>

          {/* ── Stock Hero ── */}
          <section style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 14, overflow: 'hidden' }}>
            {/* Header row */}
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #1e2d45', background: '#0f1621', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                {/* Ticker badge */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(139,92,246,0.15))',
                  border: '1px solid rgba(59,130,246,0.25)',
                  borderRadius: 10, padding: '0.375rem 0.875rem',
                }}>
                  <span style={{ fontSize: '1.375rem', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.02em', fontFamily: "'JetBrains Mono', monospace" }}>
                    {stockData.symbol}
                  </span>
                </div>
                <div>
                  <div style={{ fontSize: '0.9rem', color: '#94a3b8', fontWeight: 500, marginBottom: 2 }}>
                    {companyMetrics?.name || `${stockData.symbol} Stock`}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div className="live-dot" />
                    <span style={{ fontSize: '0.72rem', color: '#334155', fontWeight: 500 }}>Live · Tradier</span>
                  </div>
                </div>
              </div>

              {/* Price block */}
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.04em', lineHeight: 1.1, fontFamily: "'JetBrains Mono', monospace" }}>
                  {fmt$(stockData.price)}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, justifyContent: 'flex-end' }}>
                  <span style={{ fontSize: '0.9375rem', fontWeight: 700, color: changeColor, fontFamily: "'JetBrains Mono', monospace" }}>
                    {fmtΔ(stockData.change)}
                  </span>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '0.2rem 0.625rem', borderRadius: 99,
                    background: changeBg, border: `1px solid ${changeBorder}`,
                    fontSize: '0.8125rem', fontWeight: 700, color: changeColor,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>
                    {isPos ? <TrendingUp size={12} strokeWidth={2.5} /> : <TrendingDown size={12} strokeWidth={2.5} />}
                    {fmtPct(stockData.change_percent)}
                  </span>
                </div>
              </div>
            </div>

            {/* Metrics row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1px', background: '#1e2d45' }}>
              {metrics.map((m, i) => (
                <div key={i} style={{ background: '#111827', padding: '1rem 1.25rem' }}>
                  <div style={{ fontSize: '0.71rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#334155', marginBottom: '0.375rem' }}>
                    {m.label}
                  </div>
                  <div style={{
                    fontSize: '1.0625rem', fontWeight: 700,
                    color: m.color || '#e2e8f0',
                    fontFamily: m.mono ? "'JetBrains Mono', monospace" : 'inherit',
                    letterSpacing: m.mono ? '-0.01em' : 'normal',
                  }}>
                    {m.value}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ── Price Chart ── */}
          <section style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 14, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', borderBottom: '1px solid #1e2d45', background: '#0f1621' }}>
              <BarChart3 size={15} color="#3b82f6" strokeWidth={2.5} />
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Price Chart</span>
            </div>
            <div style={{ padding: '1.25rem' }}>
              <ApexStockChart symbol={stockData.symbol} />
            </div>
          </section>

          {/* ── Financial Statements ── */}
          <section style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 14, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', borderBottom: '1px solid #1e2d45', background: '#0f1621' }}>
              <FileText size={15} color="#8b5cf6" strokeWidth={2.5} />
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Financial Statements</span>
            </div>
            <div style={{ padding: '1.25rem' }}>
              <DetailedFinancialStatements symbol={stockData.symbol} />
            </div>
          </section>

          {/* ── AI Analysis + News ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem' }}>
            {/* AI Analysis */}
            <section style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', borderBottom: '1px solid #1e2d45', background: '#0f1621' }}>
                <Brain size={15} color="#a78bfa" strokeWidth={2.5} />
                <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>AI Analysis</span>
              </div>
              <div style={{ padding: '1.25rem' }}>
                <AWSAIAnalysis
                  ticker={stockData.symbol}
                  companyName={companyMetrics?.name || `${stockData.symbol} Stock`}
                  currentPrice={stockData.price}
                  marketCap={companyMetrics?.marketCap}
                />
                <p style={{ fontSize: '0.72rem', color: '#1e2d45', marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid #1a2235', lineHeight: 1.55 }}>
                  Not financial advice. AI analysis is for informational purposes only. Consult a qualified financial advisor before investing.
                </p>
              </div>
            </section>

            {/* Latest News */}
            <section style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', borderBottom: '1px solid #1e2d45', background: '#0f1621' }}>
                <Newspaper size={15} color="#10b981" strokeWidth={2.5} />
                <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Latest News</span>
              </div>
              <div style={{ padding: '1.25rem' }}>
                <StockNews
                  symbol={stockData.symbol}
                  limit={8}
                  autoRefresh={false}
                  showSentimentFilter={true}
                  compact={false}
                />
              </div>
            </section>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};
